import React, { useEffect, useState, useRef, useCallback, Suspense, lazy } from 'react';
import TaskCard from './components/TaskCard';
import DesktopDatePicker from './components/DesktopDatePicker';
import SwipeableListItem from './components/SwipeableListItem';
import LongPressReorderItem from './components/LongPressReorderItem';
import LinkifyText from './components/LinkifyText';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import './App.css';
import './components/focus.css';
import FocusPanel from './components/FocusPanel';
import { PostponeSheet, MorningPickSheet, DayCloseSheet, AddTaskSheet, TriageSheet, StaleSheet, DetailsSheet, IdeasSortSheet } from './components/FocusSheets';
import {
  dayKey, focusQueue, isOverdue, isPicked, needsDayClose, needsMorningPick, postponeDate,
  MORNING_PICK_LIMIT, emptyDayCandidates, frequentTitles, tomorrowTasks,
  overdueTasks, needsTriage, daysOverdue, staleTasks,
} from './utils/focus';

// Lazy-loaded components — split into separate chunks for faster initial load
// LandingPage is now rendered as static HTML in index.html for instant loading.
// No React component needed — see public/index.html.
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const OfferAgreement = lazy(() => import('./components/OfferAgreement'));
const OnboardingTour = lazy(() => import('./components/OnboardingTour'));
const BurgerMenu = lazy(() => import('./components/BurgerMenu'));
const JournalPage = lazy(() => import('./components/JournalPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const StatsPage = lazy(() => import('./components/StatsPage'));
const OverviewPage = lazy(() => import('./components/OverviewPage'));
const SubscriptionPage = lazy(() => import('./components/SubscriptionPage'));

const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
const API_BASE = base.endsWith('/api') ? base : `${base}/api`;
// Derive WebSocket base from API base (http->ws, https->wss)
const WS_BASE = (() => {
  const b = base.endsWith('/api') ? base.slice(0, -4) : base;
  return b.replace(/^http/, 'ws');
})();
// BOT_TOKEN removed — server-side secrets must never be included in the frontend bundle.
// Debug logging now uses console.log only.
const DEBUG = 0;

function logDebug(message) {
  console.log(message);
}

function TelegramLogin({ onLogin, onBack }) {
  const [tgId, setTgId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  const privacyUrl =
    typeof window !== 'undefined'
      ? new URL('privacy', window.location.origin).toString()
      : '/privacy';

  const handlePrivacyClick = (e) => {
    e.preventDefault();

    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(privacyUrl, { try_instant_view: false });
      return;
    }

    window.open(privacyUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    logDebug('ID login attempt for ' + tgId);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_id: tgId.trim(), password })
      });
      if (!res.ok) {
        setError('Пользователь не найден');
        logDebug('ID login failed: user not found');
        return;
      }
      const data = await res.json();
      try {
        localStorage.setItem('telegramId', String(data.telegram_id));
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
      } catch (e) {
        console.warn('Could not store auth data', e);
      }
      onLogin(data.telegram_id);
      logDebug('ID login success for ' + data.telegram_id);
    } catch (e) {
      setError('Ошибка авторизации');
      logDebug('ID login error: ' + e.message);
    }
  };

  return (
    <div className="App">
      <TaskCard>
        {onBack && (
          <button className="login-back-button" onClick={onBack} type="button">
            &larr; На главную
          </button>
        )}
        <h1>Feasy Tasker</h1>
        <form className="login-form-vertical" onSubmit={handleSubmit}>
          <input
            className="login-input"
            type="number"
            placeholder="Telegram ID"
            value={tgId}
            onChange={(e) => setTgId(e.target.value)}
            required
          />
          <input
            className="login-input"
            type="password"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button className="login-button" type="submit">Войти</button>
        </form>
        <a
          className="telegram-register-button"
          href="https://t.me/FeasyTask_bot"
          target="_blank"
          rel="noopener noreferrer"
        >
          Регистрация в телеграм боте
        </a>
        <a
          className="privacy-link"
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handlePrivacyClick}
        >
          Политика конфиденциальности
        </a>
        {error && <div className="login-error">{error}</div>}
      </TaskCard>
    </div>
  );
}

function getAuthToken() {
  try {
    return localStorage.getItem('authToken') || null;
  } catch (e) {
    return null;
  }
}

function authHeaders(extra = {}) {
  const token = getAuthToken();
  const headers = { ...extra };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function authFetch(url, options = {}) {
  const headers = authHeaders(options.headers || {});
  return fetch(url, { ...options, headers });
}

async function waitForInitData(timeout = 5000) {
  const interval = 100;
  let waited = 0;
  logDebug('Waiting for Telegram initData');
  while (!window.Telegram?.WebApp?.initData) {
    if (waited >= timeout) {
      logDebug('initData wait timed out');
      return null;
    }
    await new Promise(r => setTimeout(r, interval));
    waited += interval;
  }
  logDebug('initData available after ' + waited + 'ms');
  return window.Telegram.WebApp.initData;
}

function HomePage() {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [telegramId, setTelegramId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsRegistration, setNeedsRegistration] = useState(false);
  const [editingDateId, setEditingDateId] = useState(null);
  // Выбор даты открыт тапом по чипу повтора — подсветить поле «Повтор».
  const [repeatFocusId, setRepeatFocusId] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [ideasOrder, setIdeasOrder] = useState([]);
  const [todayOrder, setTodayOrder] = useState([]);
  const [plansOrder, setPlansOrder] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [menuPage, setMenuPage] = useState(null); // 'overview' | 'journal' | 'stats' | 'settings' | 'subscription'
  const dateRefs = useRef({});
  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(pointer: coarse)').matches;

  // NEW: состояние текущего перетаскиваемого элемента
  const [isDragging, setIsDragging] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [grippedId, setGrippedId] = useState(null);

  // Track which task has an active swipe (blocks text editing)
  const [swipingTaskId, setSwipingTaskId] = useState(null);


  // Onboarding tour for first-time users
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  const pullStartY = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const PULL_THRESHOLD = 60;

  // Drag-to-tab: refs и состояние для перетаскивания между секциями
  const tabIdeasRef = useRef(null);
  const tabTodayRef = useRef(null);
  const tabPlansRef = useRef(null);
  const [dropTargetTab, setDropTargetTab] = useState(null);

  // Экран «Фокус»: шторки, отложенные в этой сессии задачи и короткие уведомления.
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [postponeTask, setPostponeTask] = useState(null);
  const [showPick, setShowPick] = useState(false);
  const [showDayClose, setShowDayClose] = useState(false);
  const [skippedIds, setSkippedIds] = useState([]);
  // Пустой день: «Не сегодня» помним до конца дня, чтобы карточка не вернулась при перезаходе.
  const [emptySkipped, setEmptySkipped] = useState([]);
  // Удаление с отменой: задача прячется сразу, на сервер уходит через несколько секунд.
  const [hiddenIds, setHiddenIds] = useState([]);
  const deleteTimers = useRef({});
  const [streak, setStreak] = useState(0);
  const [showTriage, setShowTriage] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [showIdeasSort, setShowIdeasSort] = useState(false);
  // Удаления из разбора идей копим и отправляем при закрытии — так «Отменить» честно работает.
  const pendingIdeaDeletes = useRef(new Set());
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const autoSheetsChecked = useRef(false);
  const dropTargetTabRef = useRef(null);

  useEffect(() => {
    if (!telegramId) return;
    try {
      const load = key => {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
      };
      setIdeasOrder(load(`order_ideas_${telegramId}`));
      setTodayOrder(load(`order_today_${telegramId}`));
      setPlansOrder(load(`order_plans_${telegramId}`));
    } catch (e) {
      console.warn('Could not read stored order', e);
    }
  }, [telegramId]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('telegramId');
      if (stored) {
        setTelegramId(Number(stored));
      }
    } catch (e) {
      console.warn('Could not read stored telegram id', e);
    }
  }, []);

  // Часовой пояс — по телефону (16.10.2026). Время задачи («20:03») человек ставит по своим
  // часам, а сервер напоминает по поясу из профиля: уехал — и напоминание уходило мимо.
  useEffect(() => {
    if (!telegramId) return;
    let tz;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return; }
    if (!tz) return;
    authFetch(`${API_BASE}/reminders/settings`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data && data.timezone !== tz) {
          return postJson(`${API_BASE}/reminders/settings`, { telegram_id: telegramId, timezone: tz });
        }
        return null;
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramId]);

  // Show onboarding tour for first-time users
  useEffect(() => {
    if (!telegramId) return;
    try {
      const key = `onboarding_done_${telegramId}`;
      if (!localStorage.getItem(key)) {
        setShowOnboarding(true);
      }
    } catch (e) {
      // localStorage unavailable — skip onboarding
    }
  }, [telegramId]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (telegramId) {
      try {
        localStorage.setItem(`onboarding_done_${telegramId}`, '1');
      } catch (e) {}
    }
  };

  useEffect(() => {
    const ids = tasks.map(t => t.id);
    setIdeasOrder(o => o.filter(id => ids.includes(id)));
    setTodayOrder(o => o.filter(id => ids.includes(id)));
    setPlansOrder(o => o.filter(id => ids.includes(id)));
  }, [tasks]);

  useEffect(() => {
    if (telegramId !== null) return;
    async function fetchTelegramId() {
      logDebug("fetchTelegramId start");
      // On iOS, Telegram WebApp SDK may load initData asynchronously.
      // Only wait if we detect we're actually inside Telegram (initDataUnsafe.user is set synchronously).
      const isActuallyInTelegram = window.Telegram?.WebApp?.initDataUnsafe?.user != null;
      if (isActuallyInTelegram) {
        logDebug("Detected Telegram environment, waiting for initData (iOS fix)");
        await waitForInitData();
      }
      if (!window.Telegram || !window.Telegram.WebApp || !window.Telegram.WebApp.initData) {
        logDebug('Telegram WebApp init data not available');
        setLoading(false);
        return;
      }
      const initDataUnsafe = window.Telegram.WebApp.initDataUnsafe;
      const initData = window.Telegram.WebApp.initData;
      if (!initDataUnsafe || !initData) {
        logDebug("initDataUnsafe or initData not available");
        setLoading(false);
        return;
      }

      logDebug("initDataUnsafe: " + JSON.stringify(initDataUnsafe));
      const initDataRaw = window.Telegram?.WebApp?.initData || '';
      logDebug("Got initData: " + initDataRaw);
      try {
        const response = await fetch(`${API_BASE}/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ initData: initDataRaw })
        });
        const data = await response.json();
        logDebug("Validate response data: " + JSON.stringify(data));
        if (data && data.telegram_id) {
          logDebug("telegramId received: " + data.telegram_id);
          if (data.token) {
            try { localStorage.setItem('authToken', data.token); } catch (e) {}
          }
          setTelegramId(data.telegram_id);
        } else {
          logDebug("telegram_id not found in response");
          setTelegramId(null);
        }
      } catch (e) {
        logDebug("fetchTelegramId error: " + e.message);
        setTelegramId(null);
      } finally {
        setLoading(false);
      }
    }
    fetchTelegramId();
  }, [telegramId]);

  // Fetch tasks via REST (used for initial load, after mutations, and pull-to-refresh)
  const fetchTasksRef = useRef(null);
  useEffect(() => {
    const query = telegramId ? `telegram_id=${telegramId}` : null;
    if (!query) {
      logDebug("No identifier, skipping fetchTasks");
      fetchTasksRef.current = null;
      return;
    }
    let isMounted = true;
    const fetchTasks = () => {
      logDebug("Fetching tasks using " + query);
      authFetch(`${API_BASE}/tasks?${query}`)
        .then(async res => {
          if (res.status === 401) {
            if (isMounted) {
              try {
                localStorage.removeItem('authToken');
                localStorage.removeItem('telegramId');
              } catch (e) {}
              setTelegramId(null);
            }
            return null;
          }
          if (res.status === 404) {
            if (isMounted) {
              setNeedsRegistration(true);
              setTasks([]);
            }
            return null;
          }
          const data = await res.json();
          if (isMounted) {
            setNeedsRegistration(false);
            logDebug("Tasks received: " + JSON.stringify(data));
            setTasks(data);
          }
        });
    };
    fetchTasksRef.current = fetchTasks;
    fetchTasks(); // initial load
    return () => { isMounted = false; fetchTasksRef.current = null; };
  }, [telegramId]);

  // WebSocket connection for real-time task updates (replaces 3s polling)
  useEffect(() => {
    const token = getAuthToken();
    if (!telegramId || !token) return;

    let isMounted = true;
    let ws = null;
    let reconnectTimer = null;
    let reconnectDelay = 1000;
    const MAX_RECONNECT_DELAY = 30000;

    const connect = () => {
      if (!isMounted) return;
      const url = `${WS_BASE}/ws/tasks?token=${encodeURIComponent(token)}`;
      logDebug("WebSocket connecting: " + url);
      ws = new WebSocket(url);

      ws.onopen = () => {
        logDebug("WebSocket connected");
        reconnectDelay = 1000; // reset on successful connect
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'tasks_updated' && fetchTasksRef.current) {
            logDebug("WebSocket: tasks_updated, refreshing");
            fetchTasksRef.current();
          }
        } catch (e) {
          logDebug("WebSocket message parse error: " + e);
        }
      };

      ws.onclose = (event) => {
        logDebug("WebSocket closed: " + event.code);
        if (isMounted && event.code !== 4001) {
          // Reconnect with exponential backoff
          reconnectTimer = setTimeout(() => {
            reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
            connect();
          }, reconnectDelay);
        }
      };

      ws.onerror = () => {
        logDebug("WebSocket error");
        // onclose will fire after onerror, triggering reconnect
      };
    };

    connect();

    return () => {
      isMounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null; // prevent reconnect on intentional close
        ws.close();
      }
    };
  }, [telegramId]);

  const updateTaskStatus = async (taskId, done) => {
    if (!telegramId) {
      logDebug("No identifier, cannot change task status");
      return;
    }
    logDebug((done ? "Completing" : "Reopening") + " task: " + taskId);
    const endpoint = done ? "complete" : "pending";
    await authFetch(`${API_BASE}/tasks/${taskId}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const query = `telegram_id=${telegramId}`;
    authFetch(`${API_BASE}/tasks?${query}`)
      .then(res => res.json())
      .then(data => {
        logDebug("Tasks updated after status change: " + JSON.stringify(data));
        setTasks(data);
      });
  };

  const reloadTasks = useCallback(() => {
    if (!telegramId) return Promise.resolve();
    return authFetch(`${API_BASE}/tasks?telegram_id=${telegramId}`)
      .then(res => res.json())
      .then(data => setTasks(data));
  }, [telegramId]);

  const showToast = useCallback((text, action = null, ms = 2600) => {
    setToast({ text, action });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), ms);
  }, []);

  const loadStreak = useCallback(() => {
    if (!telegramId) return;
    authFetch(`${API_BASE}/focus/streak`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.days === 'number') setStreak(d.days); })
      .catch(() => {});
  }, [telegramId]);
  useEffect(() => { loadStreak(); }, [loadStreak]);

  const postJson = (url, body) => authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const markAsked = (prefix) => {
    try { localStorage.setItem(`${prefix}_${telegramId}_${dayKey(new Date())}`, '1'); } catch (e) {}
  };
  const wasAsked = (prefix) => {
    try { return !!localStorage.getItem(`${prefix}_${telegramId}_${dayKey(new Date())}`); } catch (e) { return false; }
  };

  // «Готово» на карточке «Сейчас»: карточка улетает сразу, сервер догоняет.
  const handleFocusDone = async (task, nextTask) => {
    const prevDue = task.due_date ? String(task.due_date).slice(0, 10) : null;
    setTasks(prev => prev.map(t => (t.id === task.id
      ? { ...t, status: 'done', completed_at: new Date().toISOString() }
      : t)));
    // «Вернуть» — как у выполнения из списка: большую кнопку тоже легко задеть.
    showToast(nextTask ? `Сделано. Дальше: ${nextTask.description}` : 'Сделано. На сегодня всё!', {
      label: 'Вернуть',
      onClick: async () => {
        setToast(null);
        await authFetch(`${API_BASE}/tasks/${task.id}/pending`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
        if (task.recurrence) await postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: prevDue });
        reloadTasks();
        loadStreak();
      },
    }, DELETE_UNDO_MS);
    try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success'); } catch (e) {}
    await authFetch(`${API_BASE}/tasks/${task.id}/complete?source=focus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    reloadTasks();
    loadStreak();
  };

  const handlePostpone = async (task, option) => {
    setPostponeTask(null);
    const due = postponeDate(option, new Date());
    if (option === 'later') setSkippedIds(prev => [...prev, task.id]);
    await postJson(`${API_BASE}/tasks/${task.id}/postpone`, { due_date: due });
    showToast(option === 'later' ? 'Отложено до вечера' : option === 'tomorrow' ? 'Перенесено на завтра' : 'Перенесено на неделю');
    reloadTasks();
  };

  // Третье «Позже»: первый шаг становится главным, сама задача уезжает на завтра.
  const handleSplit = async (task, stepText) => {
    setPostponeTask(null);
    const todayStr = dayKey(new Date());
    const res = await postJson(`${API_BASE}/tasks`, {
      description: stepText, due_date: todayStr, important: !!task.important,
    });
    let newId = null;
    try { newId = (await res.json())?.task?.id ?? null; } catch (e) {}
    await postJson(`${API_BASE}/tasks/${task.id}/postpone`, { due_date: postponeDate('tomorrow', new Date()) });
    if (newId) {
      const keep = tasks
        .filter(t => t.id !== task.id && t.status !== 'done' && isPicked(t, new Date()))
        .map(t => t.id);
      const ids = [newId, ...keep].slice(0, MORNING_PICK_LIMIT);
      await postJson(`${API_BASE}/tasks/focus`, { day: todayStr, task_ids: ids });
    }
    showToast('Первый шаг — теперь главное');
    reloadTasks();
  };

  const handleSavePick = async (ids) => {
    setShowPick(false);
    markAsked('focus_pick');
    await postJson(`${API_BASE}/tasks/focus`, { day: dayKey(new Date()), task_ids: ids });
    setSkippedIds([]);
    showToast(ids.length === 1 ? 'Главное на сегодня выбрано' : `Выбрано главных: ${ids.length}`);
    reloadTasks();
  };

  const handleMoveAllToTomorrow = async (left) => {
    setShowDayClose(false);
    markAsked('day_closed');
    const tomorrow = postponeDate('tomorrow', new Date());
    await Promise.all(left.map(t => postJson(`${API_BASE}/tasks/${t.id}/due_date`, { due_date: tomorrow })));
    showToast(`Перенесено на завтра: ${left.length}`);
    reloadTasks();
  };

  const emptySkipKey = () => `empty_skip_${telegramId}_${dayKey(new Date())}`;
  useEffect(() => {
    if (!telegramId) return;
    try { setEmptySkipped(JSON.parse(localStorage.getItem(emptySkipKey()) || '[]')); } catch (e) { setEmptySkipped([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramId]);

  const handleTakeToday = async (task) => {
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, due_date: `${dayKey(new Date())}T00:00:00` } : t)));
    showToast('Взято на сегодня');
    try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light'); } catch (e) {}
    await postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: dayKey(new Date()) });
    reloadTasks();
  };

  const handleSkipCandidate = (task) => {
    setEmptySkipped(prev => {
      const next = [...prev, task.id];
      try { localStorage.setItem(emptySkipKey(), JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  // Разбор идей по Эйзенхауэру. Важность и срок применяем сразу (оптимистично),
  // удаление — при закрытии шторки, чтобы его можно было отменить.
  const IDEA_SORT_FIELDS = {
    do: { important: true, today: true },
    plan: { important: true, today: false },
    quick: { important: false, today: true },
    later: { important: false, today: false },
  };
  const applyIdeaFields = (task, important, dueToday) => {
    const due = dueToday ? dayKey(new Date()) : null;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, important, due_date: due ? `${due}T00:00:00` : null } : t)));
    if (!!task.important !== important) postJson(`${API_BASE}/tasks/${task.id}/important`, { important });
    if (dueToday || task.due_date) postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: due });
  };
  const handleIdeaSort = (task, kind) => {
    if (kind === 'delete') {
      pendingIdeaDeletes.current.add(task.id);
      setHiddenIds(prev => [...prev, task.id]);
      return;
    }
    const f = IDEA_SORT_FIELDS[kind];
    applyIdeaFields(task, f.important, f.today);
  };
  const handleIdeaUndo = (task, kind) => {
    if (kind === 'delete') {
      pendingIdeaDeletes.current.delete(task.id);
      setHiddenIds(prev => prev.filter(id => id !== task.id));
      return;
    }
    // task — снимок до решения: возвращаем его важность и (пустой) срок
    applyIdeaFields(task, !!task.important, false);
  };
  const closeIdeasSort = async () => {
    setShowIdeasSort(false);
    const ids = [...pendingIdeaDeletes.current];
    pendingIdeaDeletes.current.clear();
    await Promise.all(ids.map(id => authFetch(`${API_BASE}/tasks/${id}`, {
      method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    })));
    await reloadTasks();
    setHiddenIds(prev => prev.filter(id => !ids.includes(id)));
  };

  const handleSaveDetails = async (task, value) => {
    const details = value.trim() || null;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, details } : t)));
    setDetailsTask(null);
    await postJson(`${API_BASE}/tasks/${task.id}/details`, { details });
    showToast(details ? 'Описание сохранено' : 'Описание убрано');
    reloadTasks();
  };

  const DELETE_UNDO_MS = 4000;
  const handleDeleteWithUndo = (task) => {
    setHiddenIds(prev => [...prev, task.id]);
    deleteTimers.current[task.id] = setTimeout(async () => {
      delete deleteTimers.current[task.id];
      await authFetch(`${API_BASE}/tasks/${task.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      await reloadTasks();
      setHiddenIds(prev => prev.filter(id => id !== task.id));
    }, DELETE_UNDO_MS);
    showToast('Удалено', {
      label: 'Вернуть',
      onClick: () => {
        clearTimeout(deleteTimers.current[task.id]);
        delete deleteTimers.current[task.id];
        setHiddenIds(prev => prev.filter(id => id !== task.id));
        setToast(null);
      },
    }, DELETE_UNDO_MS);
  };

  // Выполнение с отменой: 4 секунды на «Вернуть» — кружок легко задеть пальцем.
  // У повторяющейся задачи «выполнено» сдвигает срок, поэтому при отмене
  // возвращаем и статус, и прежнюю дату.
  const handleCompleteWithUndo = async (task, source = null) => {
    const prevDue = task.due_date ? String(task.due_date).slice(0, 10) : null;
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: 'done', completed_at: new Date().toISOString() } : t)));
    try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success'); } catch (e) {}
    const q = source ? `?source=${source}` : '';
    await authFetch(`${API_BASE}/tasks/${task.id}/complete${q}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    reloadTasks();
    showToast('Выполнено', {
      label: 'Вернуть',
      onClick: async () => {
        setToast(null);
        await authFetch(`${API_BASE}/tasks/${task.id}/pending`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
        if (task.recurrence) await postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: prevDue });
        reloadTasks();
      },
    }, DELETE_UNDO_MS);
  };

  // Действия из шторки задачи в «Обзоре».
  const handleOverviewAction = async (task, kind) => {
    if (kind === 'delete') { handleDeleteWithUndo(task); return; }
    const today = dayKey(new Date());
    if (kind === 'main') {
      const keep = tasks.filter(t => t.id !== task.id && t.status !== 'done' && isPicked(t, new Date())).map(t => t.id);
      await postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: today });
      await postJson(`${API_BASE}/tasks/focus`, { day: today, task_ids: [task.id, ...keep].slice(0, MORNING_PICK_LIMIT) });
      showToast('Теперь это главное на сегодня');
    } else {
      const due = kind === 'today' ? today : postponeDate('tomorrow', new Date());
      await postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: due });
      showToast(kind === 'today' ? 'На сегодня' : 'На завтра');
    }
    reloadTasks();
  };

  // Разбор завалов: каждое решение применяется сразу (оптимистично), список
  // перечитывается, когда шторку закрыли.
  const handleTriageAction = (task, kind) => {
    const today = `${dayKey(new Date())}T00:00:00`;
    const tomorrow = `${postponeDate('tomorrow', new Date())}T00:00:00`;
    if (kind === 'done') {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: 'done', completed_at: new Date().toISOString() } : t)));
      authFetch(`${API_BASE}/tasks/${task.id}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    } else if (kind === 'delete') {
      setTasks(prev => prev.filter(t => t.id !== task.id));
      authFetch(`${API_BASE}/tasks/${task.id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
    } else {
      const due = kind === 'today' ? today : kind === 'tomorrow' ? tomorrow : null;
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, due_date: due } : t)));
      postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: due ? due.slice(0, 10) : null });
    }
  };

  // Протухшие задачи: решения применяются сразу, список перечитывается при закрытии.
  // «Первый шаг» — шаг на сегодня, сама задача на неделю (как у третьего «Позже»,
  // но без перестройки главного: разбор не должен переставлять день).
  const handleStaleAction = (task, kind, stepText) => {
    const json = { 'Content-Type': 'application/json' };
    if (kind === 'done') {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: 'done', completed_at: new Date().toISOString() } : t)));
      authFetch(`${API_BASE}/tasks/${task.id}/complete`, { method: 'POST', headers: json });
    } else if (kind === 'release') {
      setTasks(prev => prev.filter(t => t.id !== task.id));
      authFetch(`${API_BASE}/tasks/${task.id}/release`, { method: 'POST', headers: json });
    } else if (kind === 'idea') {
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, due_date: null } : t)));
      postJson(`${API_BASE}/tasks/${task.id}/due_date`, { due_date: null });
    } else if (kind === 'step' && stepText) {
      const week = postponeDate('week', new Date());
      setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, due_date: `${week}T00:00:00` } : t)));
      postJson(`${API_BASE}/tasks`, { description: stepText, due_date: dayKey(new Date()), important: !!task.important });
      postJson(`${API_BASE}/tasks/${task.id}/postpone`, { due_date: week });
    }
  };

  const handleQuickAddToday = async (text) => {
    await postJson(`${API_BASE}/tasks`, { description: text, due_date: dayKey(new Date()) });
    showToast('Добавлено на сегодня');
    reloadTasks();
  };

  // Добавление из шторки: задача появляется в списке сразу, шторка закрывается,
  // а сервер (и ИИ у Pro) отвечает потом — список просто перечитывается. Раньше
  // текст висел в шторке, пока сервер не ответит.
  const handleAddFromSheet = async (e, dueChoice) => {
    e.preventDefault();
    const text = newTask.trim();
    if (!text || !telegramId) return;
    const now = new Date();
    const dueKey = dueChoice === 'today' ? dayKey(now)
      : dueChoice === 'tomorrow' ? postponeDate('tomorrow', now)
      : dueChoice && dueChoice !== 'none' ? dueChoice
      : null;
    const tempId = -Date.now();
    setTasks(prev => [...prev, {
      id: tempId, description: text, status: 'pending', due_date: dueKey ? `${dueKey}T00:00:00` : null,
      important: false, postpone_count: 0, focus_on: null, recurrence: null, details: null, _saving: true,
    }]);
    setNewTask('');
    setShowAddSheet(false);
    const payload = { description: text };
    if (dueKey) payload.due_date = dueKey;
    try {
      const res = await authFetch(`${API_BASE}/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      logDebug('Add task failed: ' + err);
      setTasks(prev => prev.filter(t => t.id !== tempId));
      setNewTask(text);  // текст не теряется — можно повторить
      showToast('Не сохранилось — проверьте связь и попробуйте ещё раз');
      return;
    }
    reloadTasks();
  };

  // «Важно» — ось матрицы Эйзенхауэра. Звёздочка переключается сразу, не дожидаясь
  // сервера: тап должен ощущаться мгновенным. Ошибка — откатываем перечитыванием.
  const handleToggleImportant = async (taskId, important) => {
    if (!telegramId) return;
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, important } : t)));
    try {
      const res = await authFetch(`${API_BASE}/tasks/${taskId}/important`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ important }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      logDebug('Toggle important failed: ' + e);
      authFetch(`${API_BASE}/tasks?telegram_id=${telegramId}`)
        .then(r => r.json())
        .then(data => setTasks(data));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!telegramId) return;
    await authFetch(`${API_BASE}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    const query = `telegram_id=${telegramId}`;
    authFetch(`${API_BASE}/tasks?${query}`)
      .then(res => res.json())
      .then(data => setTasks(data));
  };


  const handleDateClick = (taskId) => {
    if (swipingTaskId) return;
    setEditingDateId(prev => (prev === taskId ? null : taskId));
  };

  const handleDateChange = async (taskId, dateStr) => {
    if (!telegramId) return;
    const body = { due_date: dateStr ? dateStr : null };
    await authFetch(`${API_BASE}/tasks/${taskId}/due_date`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const query = `telegram_id=${telegramId}`;
    authFetch(`${API_BASE}/tasks?${query}`)
      .then(res => res.json())
      .then(data => setTasks(data));
    setEditingDateId(null);
  };

  // Повтор руками из выбора даты (16.10.2026).
  const handleRecurrenceChange = async (taskId, code) => {
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, recurrence: code } : t)));
    await postJson(`${API_BASE}/tasks/${taskId}/recurrence`, { recurrence: code });
    const query = `telegram_id=${telegramId}`;
    authFetch(`${API_BASE}/tasks?${query}`)
      .then(res => res.json())
      .then(data => setTasks(data));
  };

  const handleTextClick = (task) => {
    if (isDragging || swipingTaskId) return;
    setEditingTextId(task.id);
    setEditingText(task.description);
  };

  const handleTextChange = (e) => {
    setEditingText(e.target.value);
  };

  const submitTextChange = async () => {
    if (!telegramId || editingTextId === null) return;
    const body = { description: editingText };
    await authFetch(`${API_BASE}/tasks/${editingTextId}/description`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const query = `telegram_id=${telegramId}`;
    authFetch(`${API_BASE}/tasks?${query}`)
      .then(res => res.json())
      .then(data => setTasks(data));
    setEditingTextId(null);
    setEditingText('');
  };

  // Pull-to-refresh handlers
  const handlePullStart = (e) => {
    if (isDragging || refreshing) return;
    pullStartY.current = e.touches[0].clientY;
  };
  const handlePullMove = (e) => {
    if (pullStartY.current === null || isDragging || refreshing) return;
    // Only trigger pull-to-refresh when scrolled to top
    const scrollTop = e.currentTarget.scrollTop || 0;
    if (scrollTop > 0) { pullStartY.current = null; setPullDistance(0); return; }
    const dy = e.touches[0].clientY - pullStartY.current;
    if (dy > 0) {
      setPullDistance(Math.min(dy * 0.5, 100));
    }
  };
  const handlePullEnd = () => {
    if (pullDistance >= PULL_THRESHOLD && fetchTasksRef.current && !refreshing) {
      setRefreshing(true);
      fetchTasksRef.current();
      setTimeout(() => setRefreshing(false), 600);
    }
    pullStartY.current = null;
    setPullDistance(0);
  };

  // Определяет, над каким табом находится указатель
  const getTabFromPoint = useCallback((x, y) => {
    const tabs = [
      { ref: tabIdeasRef, name: 'ideas' },
      { ref: tabTodayRef, name: 'today' },
      { ref: tabPlansRef, name: 'plans' },
    ];
    for (const { ref, name } of tabs) {
      if (!ref.current) continue;
      const rect = ref.current.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return name;
      }
    }
    return null;
  }, []);

  // Глобальный трекинг указателя во время перетаскивания
  useEffect(() => {
    if (!isDragging) {
      setDropTargetTab(null);
      dropTargetTabRef.current = null;
      return;
    }
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const tab = getTabFromPoint(clientX, clientY);
      const target = (tab && tab !== activeTab && tab !== 'plans') ? tab : null;
      setDropTargetTab(target);
      dropTargetTabRef.current = target;
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('touchmove', handleMove);
    };
  }, [isDragging, activeTab, getTabFromPoint]);

  // NEW: перетаскивание — помечаем конкретный элемент
  const handleItemDragStart = (id) => {
    setIsDragging(true);
    setDraggingId(id);
    setGrippedId(id);
  };
  const handleItemDragEnd = () => {
    const targetTab = dropTargetTabRef.current;
    const taskId = draggingId;
    // Если бросили на другой таб — меняем дату
    if (targetTab && taskId) {
      if (targetTab === 'ideas') {
        // Сброс даты
        handleDateChange(taskId, null);
      } else if (targetTab === 'today') {
        // Установить дату на сегодня
        handleDateChange(taskId, isoLocal(new Date()));
      }
    }
    setDropTargetTab(null);
    dropTargetTabRef.current = null;
    setTimeout(() => setIsDragging(false), 50);
    setDraggingId(null);
    setGrippedId(null);
  };

  const handleReorder = (newOrder) => {
    const ids = newOrder.map(t => t.id);
    if (!telegramId) return;
    if (activeTab === 'ideas') {
      setIdeasOrder(ids);
      try { localStorage.setItem(`order_ideas_${telegramId}`, JSON.stringify(ids)); } catch (e) {}
    } else if (activeTab === 'today') {
      setTodayOrder(ids);
      try { localStorage.setItem(`order_today_${telegramId}`, JSON.stringify(ids)); } catch (e) {}
    } else if (activeTab === 'plans') {
      setPlansOrder(ids);
      try { localStorage.setItem(`order_plans_${telegramId}`, JSON.stringify(ids)); } catch (e) {}
    }
  };

  // Спрятанные (удаление ждёт отмены или закрытия разбора) не показываем нигде.
  const pendingTasks = tasks.filter(t => t.status === 'pending' && !hiddenIds.includes(t.id));
  const doneTasks = tasks
    .filter(t => t.status === 'done')
    .sort((a, b) => b.id - a.id);


  const handleLogout = () => {
    try {
      localStorage.removeItem('telegramId');
      localStorage.removeItem('authToken');
    } catch (e) {
      console.warn('Could not remove stored auth data', e);
    }
    setTelegramId(null);
  };

  function getDateFromTask(task) {
    if (task.due_date) {
      const d = new Date(task.due_date);
      if (!isNaN(d)) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    if (!task.tags) return null;
    for (const tag of task.tags) {
      if (typeof tag === 'string') {
        const m = tag.match(/^\u0414\u0430\u0442\u0430[:\s]*(\d{4}-\d{2}-\d{2})$/); // "Дата"
        if (m) {
          const d = new Date(m[1]);
          if (!isNaN(d)) return d;
        }
      } else if (tag.name === 'Дата' && tag.value) {
        const d = new Date(tag.value);
        if (!isNaN(d)) return d;
      }
    }
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function formatDate(date) {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${d}.${m}`;
  }

  // Время задачи «ЧЧ:ММ»; ровно 00:00 — «без времени», так понимают все клиенты.
  function taskTime(task) {
    const m = /T(\d{2}):(\d{2})/.exec(task.due_date || '');
    return m && !(m[1] === '00' && m[2] === '00') ? `${m[1]}:${m[2]}` : '';
  }

  function isoLocal(date) {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split('T')[0];
  }

  function formatInputDate(date) {
    return isoLocal(date);
  }

  const WEEKDAY_LABELS = { mon: 'пн', tue: 'вт', wed: 'ср', thu: 'чт', fri: 'пт', sat: 'сб', sun: 'вс' };
  const NTH_LABELS = { 1: '1-й', 2: '2-й', 3: '3-й', 4: '4-й', 5: '5-й' };
  const WEEKDAY_FULL = { mon: 'понедельник', tue: 'вторник', wed: 'среду', thu: 'четверг', fri: 'пятницу', sat: 'субботу', sun: 'воскресенье' };

  function formatRecurrence(rec) {
    if (!rec) return '';
    if (rec === 'daily') return 'Каждый день';
    if (rec === 'weekdays:mon,tue,wed,thu,fri') return 'По будням';
    if (rec === 'every_N_weeks:2') return 'Раз в две недели';
    if (rec === 'weekly') return 'Каждую неделю';
    if (rec === 'monthly') return 'Каждый месяц';
    if (rec === 'yearly') return 'Каждый год';
    if (rec.startsWith('every_N_days:')) {
      const n = rec.split(':')[1];
      const k = Number(n);
      const w = k % 10 === 1 && k % 100 !== 11 ? 'день' : [2, 3, 4].includes(k % 10) && ![12, 13, 14].includes(k % 100) ? 'дня' : 'дней';
      return `Каждые ${n} ${w}`;
    }
    if (rec.startsWith('every_N_weeks:')) {
      const n = rec.split(':')[1];
      const k = Number(n);
      const w = k % 10 === 1 && k % 100 !== 11 ? 'неделю' : [2, 3, 4].includes(k % 10) && ![12, 13, 14].includes(k % 100) ? 'недели' : 'недель';
      return `Каждые ${n} ${w}`;
    }
    if (rec.startsWith('monthly_nth:')) {
      const parts = rec.split(':');
      const n = parts[1];
      const day = parts[2];
      return `${NTH_LABELS[n] || n} ${WEEKDAY_FULL[day] || day} мес.`;
    }
    if (rec.startsWith('weekdays:')) {
      const days = rec.slice('weekdays:'.length).split(',');
      return days.map(d => WEEKDAY_LABELS[d.trim()] || d).join(', ');
    }
    return rec;
  }

  function orderList(list, order) {
    const map = new Map(list.map(t => [t.id, t]));
    const result = [];
    order.forEach(id => {
      const t = map.get(id);
      if (t) {
        result.push(t);
        map.delete(id);
      }
    });
    map.forEach(t => result.push(t));
    return result;
  }

  const ideasTasks = pendingTasks.filter(t => !getDateFromTask(t));
  const todayTasks = pendingTasks.filter(t => {
    const d = getDateFromTask(t);
    return d && d <= today;
  });
  const planTasks = pendingTasks.filter(t => {
    const d = getDateFromTask(t);
    return d && d > today;
  });

  const todayDoneTasks = doneTasks.filter(t => {
    if (!t.completed_at) return false;
    const d = new Date(t.completed_at);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  });

  const orderedIdeas = orderList(ideasTasks, ideasOrder);
  const orderedToday = orderList(todayTasks, todayOrder)
    .map((t, idx) => ({ t, idx, d: getDateFromTask(t) }))
    .sort((a, b) => {
      const aOverdue = a.d < today;
      const bOverdue = b.d < today;
      if (aOverdue && bOverdue) return a.d - b.d;
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
      return a.idx - b.idx;
    })
    .map(o => o.t);
  const orderedPlans = orderList(planTasks, plansOrder)
    .slice()
    .sort((a, b) => {
      const da = getDateFromTask(a);
      const db = getDateFromTask(b);
      return da - db;
    });

  // Фокус: главная задача — первая в очереди, остальное идёт в «Дальше».
  const nowDate = new Date();
  const queue = focusQueue(orderedToday, nowDate, skippedIds);
  const focusTask = queue[0] || null;
  const nextFocusTask = queue[1] || null;
  const overdueCount = todayTasks.filter(t => isOverdue(t, nowDate)).length;
  const pickedTodayIds = todayTasks.filter(t => isPicked(t, nowDate)).map(t => t.id);
  const orderedTodayRest = focusTask ? orderedToday.filter(t => t.id !== focusTask.id) : orderedToday;
  const visibleAll = tasks.filter(t => !hiddenIds.includes(t.id));
  const triageList = overdueTasks(tasks, nowDate);
  const staleList = staleTasks(visibleAll, nowDate);
  const emptyDay = focusTask ? null : {
    candidates: emptyDayCandidates(visibleAll, nowDate, emptySkipped),
    suggestions: frequentTitles(tasks, 4, nowDate),
    tomorrow: tomorrowTasks(tasks, nowDate),
    onTake: handleTakeToday,
    onSkip: handleSkipCandidate,
    onQuickAdd: handleQuickAddToday,
    onDelete: handleDeleteWithUndo,
    onSortIdeas: ideasTasks.length ? () => setShowIdeasSort(true) : null,
  };

  const ideasCount = ideasTasks.length;
  const todayCount = todayTasks.length;
  const plansCount = planTasks.length;

  let visibleTasks = orderedIdeas;
  if (activeTab === 'today') visibleTasks = orderedTodayRest;
  if (activeTab === 'plans') visibleTasks = orderedPlans;

  const inTelegram =
    window.Telegram &&
    window.Telegram.WebApp &&
    window.Telegram.WebApp.initData &&
    window.Telegram.WebApp.initData !== '';
  // Detect Telegram environment even when initData is empty (iOS race condition).
  // initDataUnsafe.user is set synchronously by the SDK when actually inside Telegram.
  const inTelegramApp =
    window.Telegram?.WebApp?.initDataUnsafe?.user != null;

  // Sync static landing / React root visibility.
  // Must be called before any early returns to satisfy Rules of Hooks.
  useEffect(() => {
    const staticLanding = document.getElementById('static-landing');
    const rootEl = document.getElementById('root');
    if (!staticLanding) return;
    // Show static landing only when: not loading, no telegramId, not in Telegram
    const shouldShowStatic = !loading && !telegramId && !inTelegram && !inTelegramApp;
    staticLanding.style.display = shouldShowStatic ? 'block' : 'none';
    if (rootEl) rootEl.style.display = shouldShowStatic ? 'none' : 'block';
    // Тёмная тема — для всего, что рисует React; у лендинга свой светлый фон.
    document.body.classList.toggle('app-dark', !shouldShowStatic);
    document.documentElement.classList.toggle('app-dark', !shouldShowStatic);
  });

  // Тёмный фон под главным экраном и в шапке Telegram, пока открыт таскер.
  useEffect(() => {
    if (!telegramId) return undefined;
    document.body.classList.add('focus-body');
    document.documentElement.classList.add('focus-body');
    try {
      window.Telegram?.WebApp?.setHeaderColor?.('#101114');
      window.Telegram?.WebApp?.setBackgroundColor?.('#101114');
    } catch (e) {}
    return () => {
      document.body.classList.remove('focus-body');
      document.documentElement.classList.remove('focus-body');
    };
  }, [telegramId]);

  // Утренний выбор и итог дня открываются сами — один раз за день и только на «Сегодня».
  useEffect(() => {
    if (loading || !telegramId || autoSheetsChecked.current) return;
    if (!tasks.length) return;
    autoSheetsChecked.current = true;
    if (activeTab !== 'today' || menuPage || showOnboarding) return;
    const now = new Date();
    // Пришли по кнопке «Разобрать в приложении» из понедельничного сообщения бота.
    if (new URLSearchParams(window.location.search).get('stale') === '1' && staleList.length) {
      setShowStale(true);
      return;
    }
    if (needsDayClose(todayTasks, now, wasAsked('day_closed'))) {
      setShowDayClose(true);
    } else if (now.getHours() < 19 && needsMorningPick(todayTasks, now, wasAsked('focus_pick'))) {
      setShowPick(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, telegramId, tasks]);

  const suspenseFallback = (
    <div className="loading-container">
      <div className="spinner" />
    </div>
  );

  // Route: privacy policy page
  if (window.location.pathname.includes('privacy')) {
    return <Suspense fallback={suspenseFallback}><PrivacyPolicy /></Suspense>;
  }

  // Route: offer agreement page
  if (window.location.pathname.includes('offer')) {
    return <Suspense fallback={suspenseFallback}><OfferAgreement /></Suspense>;
  }

  if (loading) {
    return suspenseFallback;
  }
  if (!telegramId && !inTelegram && !inTelegramApp) {
    // Landing is already rendered as static HTML in index.html — no React needed
    return null;
  }
  if (!telegramId && (inTelegram || inTelegramApp)) {
    return <TelegramLogin onLogin={(id) => {
      try { localStorage.setItem('telegramId', String(id)); } catch (e) {}
      setTelegramId(id);
    }} />;
  }
  if (needsRegistration) {
    return (
      <TaskCard>
        <p className="register-message">
          сначала запустите бота через команду /start
        </p>
      </TaskCard>
    );
  }

  return (
    <>
    <TaskCard className="focus-theme">
      <div className="header">
        <button
          className="hamburger-button"
          onClick={() => setShowBurgerMenu(true)}
          aria-label="Меню"
        >
          <svg
            className="hamburger-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <h1>{nowDate.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}</h1>
        <button
          className="overview-button"
          onClick={() => setMenuPage('overview')}
          aria-label="Обзор: неделя и матрица важности"
          title="Обзор"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <rect x="2" y="2" width="8" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
            <rect x="12" y="2" width="8" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
            <rect x="2" y="12" width="8" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
            <rect x="12" y="12" width="8" height="8" rx="2.5" fill="currentColor" opacity="0.35" stroke="currentColor" strokeWidth="1.8"/>
          </svg>
        </button>
        {!inTelegram && telegramId && (
          <button className="logout-button" onClick={handleLogout}>
            Выйти
          </button>
        )}
      </div>
      {activeTab === 'today' ? (
        <>
          <FocusPanel
            task={focusTask}
            today={nowDate}
            doneCount={todayDoneTasks.length}
            leftCount={todayTasks.length}
            overdueCount={overdueCount}
            pickedCount={pickedTodayIds.length}
            onDone={(t) => handleFocusDone(t, nextFocusTask)}
            onLater={(t) => setPostponeTask(t)}
            onOpenDayClose={() => setShowDayClose(true)}
            onOpenPick={() => setShowPick(true)}
            emptyDay={emptyDay}
            streak={streak}
            triageCount={needsTriage(tasks, nowDate) ? triageList.length : 0}
            onOpenTriage={() => setShowTriage(true)}
            onDateChange={handleDateChange}
            onRecurrence={handleRecurrenceChange}
            formatRecurrence={formatRecurrence}
            staleCount={staleList.length}
            onOpenStale={() => setShowStale(true)}
            onOpenDetails={(t) => setDetailsTask(t)}
          />
          {visibleTasks.length > 0 && <div className="section-label">Дальше</div>}
        </>
      ) : (
        <>
          <div className="section-label">{activeTab === 'ideas' ? 'Идеи — без даты' : 'Планы'}</div>
          {activeTab === 'ideas' && ideasTasks.length > 0 && (
            <div className="ideas-cta">
              <span>Разложить идеи по важности и срочности — по одной карточке.</span>
              <button type="button" className="focus-btn focus-btn-primary" onClick={() => setShowIdeasSort(true)}>
                Разобрать · {ideasTasks.length}
              </button>
            </div>
          )}
        </>
      )}
      {(pullDistance > 0 || refreshing) && (
        <div className="pull-to-refresh-indicator" style={{ height: refreshing ? 30 : pullDistance * 0.4, overflow: 'hidden', textAlign: 'center', transition: refreshing ? 'height 0.3s' : 'none' }}>
          <span style={{ fontSize: 12, color: '#8B8F98' }}>
            {refreshing ? '...' : pullDistance >= PULL_THRESHOLD ? '↑' : '↓'}
          </span>
        </div>
      )}
      <Reorder.Group
        as="ul"
        className="task-list"
        axis="y"
        values={visibleTasks}
        onReorder={handleReorder}
        onTouchStart={handlePullStart}
        onTouchMove={handlePullMove}
        onTouchEnd={handlePullEnd}
      >
        <AnimatePresence>
          {visibleTasks.map(task => {
            const taskDate = getDateFromTask(task);
            const isOverdue =
              activeTab === 'today' && taskDate && taskDate < today;
            const dateRef =
              dateRefs.current[task.id] || (dateRefs.current[task.id] = React.createRef());
            const showPicker = editingDateId === task.id;
            const isThisDragging = draggingId === task.id; // NEW
            const isGripped = grippedId === task.id; // NEW

            return (
              <LongPressReorderItem
                as="li"
                value={task}
                key={task.id}
                layout
                layoutTransition={false}
                dragTransition={{ bounceStiffness: 100000, bounceDamping: 100000 }}
                inTelegram={inTelegram}
                className={`${isOverdue ? 'overdue' : ''} ${isGripped ? 'gripped' : ''} ${isThisDragging ? 'dragging' : ''}`} // NEW
                onPointerDown={() => setGrippedId(task.id)}   // NEW
                onPointerUp={() => setGrippedId(null)}        // NEW
                onPointerCancel={() => setGrippedId(null)}    // NEW
                onDragStart={() => handleItemDragStart(task.id)} // NEW
                onDragEnd={handleItemDragEnd}                     // NEW
                disableReorder={!!swipingTaskId}
              >
                <SwipeableListItem
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ type: "spring", duration: 0.35 }}
                  onDelete={() => handleDeleteWithUndo(task)}
                  disableSwipe={isDragging || task._saving}
                  onSwipeStateChange={(swiping) => setSwipingTaskId(swiping ? task.id : null)}
                >
                  <div className={`task-row ${task._saving ? 'is-saving' : ''}`}>
                  <input
                    className="task-checkbox"
                    type="checkbox"
                    checked={false}
                    onChange={() => handleCompleteWithUndo(task)}
                  />
                  {editingTextId === task.id ? (
                    <input
                      className="task-text-input"
                      autoFocus
                      value={editingText}
                      onChange={handleTextChange}
                      onBlur={submitTextChange}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitTextChange();
                        } else if (e.key === 'Escape') {
                          setEditingTextId(null);
                        }
                      }}
                    />
                  ) : (
                    <div className="task-main">
                      <LinkifyText
                        text={task.description}
                        className="task-text"
                        onClick={() => handleTextClick(task)}
                      />
                      {task.details && (
                        <button type="button" className="task-details-preview" onClick={() => setDetailsTask(task)}>
                          <span className="task-details-text">{task.details}</span>
                        </button>
                      )}
                    </div>
                  )}
                  {task.recurrence && (
                    // Чип повтора кликабельный: открывает выбор даты с полем «Повтор» (16.10.2026).
                    <button
                      type="button"
                      className="recurrence-badge is-button"
                      onClick={() => { setRepeatFocusId(task.id); handleDateClick(task.id); }}
                      aria-label={`Повтор: ${formatRecurrence(task.recurrence)}. Изменить`}
                    >
                      {formatRecurrence(task.recurrence)}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`task-star ${task.important ? 'is-active' : ''}`}
                    onClick={() => handleToggleImportant(task.id, !task.important)}
                    aria-label={task.important ? 'Снять пометку «важно»' : 'Пометить как важное'}
                    aria-pressed={!!task.important}
                  >
                    {task.important ? '★' : '☆'}
                  </button>
                  <span
                    ref={dateRef}
                    className={"task-date" + (taskDate ? "" : " no-date")}
                    onClick={() => { setRepeatFocusId(null); handleDateClick(task.id); }}
                    style={{}}
                  >
                    {task._saving ? <span className="task-saving-label">сохраняю…</span>
                      : taskDate ? formatDate(taskDate) + (taskTime(task) ? ` · ${taskTime(task)}` : '') : "Без даты"}
                  </span>
                  {showPicker && (
                    <DesktopDatePicker
                      anchorRef={dateRef}
                      date={taskDate ? formatInputDate(taskDate) : undefined}
                      time={taskTime(task)}
                      recurrence={task.recurrence}
                      highlightRepeat={repeatFocusId === task.id}
                      onRecurrence={code => handleRecurrenceChange(task.id, code)}
                      onSelect={dateStr =>
                        handleDateChange(
                          task.id,
                          dateStr
                        )
                      }
                      onBlur={() => { setEditingDateId(null); setRepeatFocusId(null); }}
                    />
                  )}
                </div>
                </SwipeableListItem>
              </LongPressReorderItem>
            );
          })}
        </AnimatePresence>
        {visibleTasks.length === 0 && activeTab !== 'today' && (
          <li className="no-tasks-message">
            {activeTab === 'ideas' && 'Нет идей — напишите задачу боту или нажмите + выше'}
            {activeTab === 'today' && 'На сегодня задач нет — перетащите задачу сюда или задайте дату'}
            {activeTab === 'plans' && 'Нет запланированных задач — задайте дату, чтобы задача появилась здесь'}
          </li>
        )}
      </Reorder.Group>
      {activeTab === 'today' && todayDoneTasks.length > 0 && (
        <>
          <div className="done-divider">
            <span className="done-divider-line" />
            <span className="done-divider-text">
              За сегодня выполнено {todayDoneTasks.length}{' '}
              {todayDoneTasks.length === 1 ? 'задача' : todayDoneTasks.length >= 2 && todayDoneTasks.length <= 4 ? 'задачи' : 'задач'}
            </span>
            <span className="done-divider-line" />
          </div>
          <motion.ul className="task-list task-list-done" layout>
            <AnimatePresence>
              {todayDoneTasks.map(task => {
                const taskDate = getDateFromTask(task);
                return (
                  <SwipeableListItem
                    layout
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", duration: 0.35 }}
                    onDelete={() => handleDeleteWithUndo(task)}
                    disableSwipe={isDragging}
                  >
                    <div className="task-row">
                      <input
                        className="task-checkbox"
                        type="checkbox"
                        checked={true}
                        onChange={e => updateTaskStatus(task.id, e.target.checked)}
                      />
                      <LinkifyText text={task.description} className="task-text task-done-text" />
                      {task.recurrence && (
                        <span className="recurrence-badge">
                          {formatRecurrence(task.recurrence)}
                        </span>
                      )}
                      <span
                        className={"task-date" + (taskDate ? "" : " no-date")}
                      >
                        {taskDate ? formatDate(taskDate) : ""}
                      </span>
                    </div>
                  </SwipeableListItem>
                );
              })}
            </AnimatePresence>
          </motion.ul>
        </>
      )}
      <nav className="focus-dock" aria-label="Разделы">
        <div className="dock-seg">
          <button
            ref={tabIdeasRef}
            className={`dock-tab ${activeTab === 'ideas' ? 'active' : ''} ${dropTargetTab === 'ideas' ? 'drop-target' : ''}`}
            onClick={() => setActiveTab('ideas')}
          >
            Идеи {ideasCount > 0 && <span className="dock-count">{ideasCount}</span>}
          </button>
          <button
            ref={tabTodayRef}
            className={`dock-tab ${activeTab === 'today' ? 'active' : ''} ${dropTargetTab === 'today' ? 'drop-target' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            Сегодня {todayCount > 0 && <span className="dock-count">{todayCount}</span>}
          </button>
          <button
            ref={tabPlansRef}
            className={`dock-tab ${activeTab === 'plans' ? 'active' : ''} ${dropTargetTab === 'plans' ? 'drop-target' : ''}`}
            onClick={() => setActiveTab('plans')}
          >
            Планы {plansCount > 0 && <span className="dock-count">{plansCount}</span>}
          </button>
        </div>
        <button className="dock-plus" onClick={() => setShowAddSheet(true)} aria-label="Новая задача">+</button>
      </nav>

      <AddTaskSheet
        open={showAddSheet}
        value={newTask}
        onChange={setNewTask}
        onClose={() => setShowAddSheet(false)}
        onSubmit={handleAddFromSheet}
        tab={activeTab}
      />
      <PostponeSheet
        task={postponeTask}
        onClose={() => setPostponeTask(null)}
        onPostpone={handlePostpone}
        onSplit={handleSplit}
      />
      <MorningPickSheet
        open={showPick}
        tasks={todayTasks}
        initial={pickedTodayIds}
        onClose={() => { setShowPick(false); markAsked('focus_pick'); }}
        onSave={handleSavePick}
      />
      <IdeasSortSheet
        open={showIdeasSort}
        ideas={[...ideasTasks].sort((a, b) => a.id - b.id)}
        onClose={closeIdeasSort}
        onSort={handleIdeaSort}
        onUndo={handleIdeaUndo}
      />
      <DetailsSheet
        task={detailsTask}
        onClose={() => setDetailsTask(null)}
        onSave={handleSaveDetails}
        LinkifyText={LinkifyText}
      />
      <StaleSheet
        open={showStale}
        tasks={staleList}
        daysOverdue={(t) => daysOverdue(t, nowDate)}
        onClose={() => { setShowStale(false); reloadTasks(); }}
        onAction={handleStaleAction}
      />
      <TriageSheet
        open={showTriage}
        tasks={triageList}
        daysOverdue={(t) => daysOverdue(t, nowDate)}
        onClose={() => { setShowTriage(false); reloadTasks(); }}
        onAction={handleTriageAction}
      />
      <DayCloseSheet
        open={showDayClose}
        doneCount={todayDoneTasks.length}
        left={todayTasks}
        onClose={() => { setShowDayClose(false); markAsked('day_closed'); }}
        onMoveAll={() => handleMoveAllToTomorrow(todayTasks)}
      />
      <AnimatePresence>
        {toast && (
          <motion.div
            className="focus-toast"
            role="status"
            initial={{ opacity: 0, y: 12, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 12, x: '-50%' }}
          >
            <span className="focus-toast-text">{toast.text}</span>
            {toast.action && (
              <button type="button" className="focus-toast-action" onClick={toast.action.onClick}>
                {toast.action.label}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </TaskCard>
    <Suspense fallback={null}>
      {showOnboarding && (
        <OnboardingTour onComplete={handleOnboardingComplete} />
      )}
      <BurgerMenu
        isOpen={showBurgerMenu}
        onClose={() => setShowBurgerMenu(false)}
        onNavigate={(page) => setMenuPage(page)}
        currentPage={menuPage}
      />
      <AnimatePresence>
        {menuPage === 'overview' && (
          <OverviewPage
            tasks={tasks.filter(t => !hiddenIds.includes(t.id))}
            onClose={() => setMenuPage(null)}
            onComplete={handleCompleteWithUndo}
            onToggleImportant={handleToggleImportant}
            onAction={handleOverviewAction}
          />
        )}
        {menuPage === 'journal' && (
          <JournalPage
            doneTasks={doneTasks}
            onClose={() => setMenuPage(null)}
            onToggleStatus={updateTaskStatus}
            onDelete={handleDeleteTask}
            formatDate={formatDate}
            getDateFromTask={getDateFromTask}
            formatRecurrence={formatRecurrence}
          />
        )}
        {menuPage === 'stats' && (
          <StatsPage
            tasks={tasks}
            onClose={() => setMenuPage(null)}
          />
        )}
        {menuPage === 'settings' && (
          <SettingsPage
            onClose={() => setMenuPage(null)}
            apiBase={API_BASE}
            authFetch={authFetch}
          />
        )}
        {menuPage === 'subscription' && (
          <SubscriptionPage
            onClose={() => setMenuPage(null)}
            apiBase={API_BASE}
            authFetch={authFetch}
          />
        )}
      </AnimatePresence>
    </Suspense>
    </>
  );
}

export default HomePage;
